export {
  OmnithermError,
  OmnithermErrorCode,
  print,
  ThermalDocument,
  WebBluetoothTransport,
  isQzAvailable,
  isWebUsbAvailable,
  isWebBluetoothAvailable,
  isWebSerialAvailable,
  listQzPrinters,
} from 'bizpluspos-print-core/web';

export type { ConnectionConfig } from 'bizpluspos-print-core/web';

/** Stub — native BLE keep-alive is never used on web. */
export class NativeBleTransport {
  async connect(_config?: unknown): Promise<void> {
    throw new Error('Native BLE is not available on web');
  }

  async write(_data?: Uint8Array): Promise<void> {
    throw new Error('Native BLE is not available on web');
  }

  async disconnect(): Promise<void> {}

  isConnected(): boolean {
    return false;
  }
}

export type BleDevice = {
  inner_mac_address: string;
  device_name?: string;
  inner_name?: string;
};

export type UsbDevice = {
  vendor_id: number;
  product_id: number;
  product_name?: string;
};

export type SerialPortDevice = {
  usb_vendor_id?: number;
  usb_product_id?: number;
  label: string;
};

export async function scanBleDevices(): Promise<BleDevice[]> {
  return [];
}

export async function getBondedBleDevices(): Promise<BleDevice[]> {
  return [];
}

export type DiscoveredBleDevice = {
  inner_mac_address: string;
  device_name: string | null;
  is_bonded: boolean;
};

export type DiscoverBleOptions = {
  duration?: number;
};

export async function discoverBleDevices(
  _opts?: DiscoverBleOptions,
): Promise<DiscoveredBleDevice[]> {
  return [];
}

export async function ensureNativeBleReady(
  _macAddress?: string,
): Promise<void> {}

export async function loadBlePrinter(): Promise<never> {
  throw new Error('Native Bluetooth printing is not available on web');
}

const USB_CLASS_PRINTER = 7;
const USB_CLASS_VENDOR_SPECIFIC = 0xff;
const PRINTER_NAME_PATTERN =
  /\b(printer|thermal|receipt|esc\/?pos|pos|epson|xprinter|rongta|gprinter|hprt|zjiang|bixolon|star|citizen|munbyn|cashino|goojprt|sewoo|sunmi)\b/i;

function getUsbInterfaceClasses(device: any): number[] {
  const configs = [
    device?.configuration,
    ...(Array.isArray(device?.configurations) ? device.configurations : []),
  ].filter(Boolean);

  return configs.flatMap((config: any) =>
    (Array.isArray(config?.interfaces) ? config.interfaces : []).flatMap(
      (iface: any) => {
        const alternates = [
          iface?.alternate,
          ...(Array.isArray(iface?.alternates) ? iface.alternates : []),
        ].filter(Boolean);
        return alternates
          .map((alternate: any) => Number(alternate?.interfaceClass))
          .filter(Number.isFinite);
      },
    ),
  );
}

function isLikelyUsbPrinter(device: any): boolean {
  try {
    const interfaceClasses = getUsbInterfaceClasses(device);
    if (interfaceClasses.includes(USB_CLASS_PRINTER)) return true;

    const name = [
      device?.productName,
      device?.manufacturerName,
      device?.serialNumber,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      interfaceClasses.includes(USB_CLASS_VENDOR_SPECIFIC) &&
      PRINTER_NAME_PATTERN.test(name)
    );
  } catch {
    return false;
  }
}

/**
 * @param requestAccess  When true AND no previously-authorized devices exist,
 *   opens the browser's USB device picker (requires a user gesture).
 *   Pass false for silent/background scans to avoid an unexpected popup.
 */
export async function scanUsbDevices(
  requestAccess = true,
): Promise<UsbDevice[]> {
  if (typeof navigator === 'undefined' || !('usb' in navigator)) return [];
  try {
    const usb = (navigator as any).usb;
    let devices: any[] = await usb.getDevices();

    if (!devices.length && requestAccess) {
      try {
        const picked = await usb.requestDevice({
          filters: [], // show all devices in picker
        });
        if (picked) {
          await picked.open();
          if (!isLikelyUsbPrinter(picked)) {
            await picked.close();
            throw new Error(
              `"${picked.productName || 'Selected device'}" does not appear to be a printer.`,
            );
          }
          await picked.close();
          devices = [picked];
        }
      } catch (e) {
        if (
          e instanceof Error &&
          e.message.includes('does not appear to be a printer')
        ) {
          throw e;
        }
        // User cancelled the picker — that's fine, return empty.
      }
    }

    return devices.filter(isLikelyUsbPrinter).map((d: any) => ({
      vendor_id: d.vendorId as number,
      product_id: d.productId as number,
      product_name: (d.productName || 'USB Printer') as string,
    }));
  } catch (e) {
    if (
      e instanceof Error &&
      e.message.includes('does not appear to be a printer')
    ) {
      throw e;
    }
    return [];
  }
}

/**
 * @param requestAccess  When true, opens the browser's serial port picker
 *   so users can authorize another port (requires a user gesture).
 *   Pass false for silent/background scans to avoid an unexpected popup.
 */
export async function scanSerialPorts(
  requestAccess = true,
): Promise<SerialPortDevice[]> {
  if (typeof navigator === 'undefined' || !('serial' in navigator)) return [];
  try {
    const serial = (navigator as any).serial;
    let ports: any[] = requestAccess ? [] : await serial.getPorts();

    if (requestAccess) {
      try {
        const picked = await serial.requestPort();
        if (picked) ports = [picked];
      } catch {
        // User cancelled the picker — that's fine, return empty.
      }
    }

    return ports.map((port: any, index: number) => {
      const info = port.getInfo?.() ?? {};
      const vendorId = info.usbVendorId as number | undefined;
      const productId = info.usbProductId as number | undefined;
      const suffix =
        vendorId != null && productId != null
          ? `VID ${vendorId} / PID ${productId}`
          : `Port ${index + 1}`;
      return {
        usb_vendor_id: vendorId,
        usb_product_id: productId,
        label: `Serial ${suffix}`,
      };
    });
  } catch {
    return [];
  }
}
